
import { defineComponent } from "vue";
import { mapState } from "vuex";

import Loader from "@/components/Loader.vue";
import RGPDLink from "@/components/RGPDLink.vue";
import GcuLink from "@/components/gcuLink.vue";
import RGPDPopin from "@/components/RGPDPopin.vue";
import { State } from "@/store";
import Consents from "@/models/Consents";
import RemindsMeCheckbox from "@/components/RemindsMeCheckbox.vue";

export default defineComponent({
    name: "Signup",
    components: {
        Loader,
        GcuLink,
        RGPDLink,
        RGPDPopin,
        RemindsMeCheckbox
    },
    data() {
        return {
            name: "",
            email: "",
            password1: "",
            password2: "",
            passType: `password`,
            isPassHidden: true,
            isPending: false,
            error: "",
            messageTitle: "",
            messageText: "",
            messagePromiseResolve: null as Function | null,
            rgpdPopinDisplayed: false,
            termsAndConditionsConsent: null,
            shareEmailConsent: null,
            remindsMe: false,
            passwordInvalid: false,
            passwordValidations: {
                pwdLength: false,
                pwdNumberRequired: false,
                pwdUpperRequired: false,
                pwdNoSpace: false
            },
            pwdNoMatch: false
        };
    },
    computed: {
        formInvalid() {
            return (
                !this.name.length ||
                !this.email.length ||
                !this.password1.length ||
                this.passwordInvalid ||
                this.pwdNoMatch
            );
        },
        ...mapState({
            qrcodeObject: (state: State) => state.qrcode.qrcodeObject,
            authenticated: (state: State) => state.auth.authenticated,
            didSignUp: (state: State) => state.auth.didSignUp
        })
    },
    watch: {
        async authenticated() {
            if (this.authenticated) {
                this.isPending = false;

                if (this.didSignUp) {
                    await this.$store.dispatch("rgpd/updateConsents", {
                        qrcodeId: this.qrcodeObject.qrcodeId,
                        shareEmailConsent: this.shareEmailConsent,
                        termsAndConditionsConsent: this.termsAndConditionsConsent
                    });

                    if (
                        this.$t("signInUp.onSignUpPopinText").length ||
                        this.$t("signInUp.onSignUpPopinTitle")
                    ) {
                        this.displayMessage(
                            this.$t("signInUp.onSignUpPopinText"),
                            this.$t("signInUp.onSignUpPopinTitle")
                        ).then(() => {
                            this.$router.replace("Upload");
                        });
                    } else {
                        this.$router.replace("Upload");
                    }
                } else {
                    this.$router.replace("Upload");
                }
            }
        },

        password1(value) {
            this.validatePwd(value);
        },

        password2() {
            this.pwdNoMatch = this.password1 !== this.password2;
        }
    },
    mounted() {
        if (!this.qrcodeObject.isOpen) {
            this.$router.replace("/signin");
        }
    },
    methods: {

        async submitForm() {
            this.displayRGPDPopin();
        },

        showPassword() {
            if(this.isPassHidden) {
                this.passType = `text`;
                this.isPassHidden = false;
            } else {
                this.passType = `password`;
                this.isPassHidden = true;
            }
        },

        displayMessage(text: string, title = ""): Promise<void> {
            return new Promise(resolve => {
                this.messagePromiseResolve = resolve;
                this.messageText = text;
                this.messageTitle = title;
            });
        },

        closeMessagePopin() {
            this.messageText = "";
            this.messageTitle = "";

            if (this.messagePromiseResolve) {
                this.messagePromiseResolve();
                this.messagePromiseResolve = null;
            }
        },

        displayRGPDPopin() {
            this.rgpdPopinDisplayed = true;
        },

        async onRGPDClosed(consents: Consents) {
            this.rgpdPopinDisplayed = false;

            if (!consents.termsAndConditionsConsent) {
                this.displayRGPDPopin();
                return;
            }

            this.error = "";
            this.isPending = true;
            try {
                this.termsAndConditionsConsent = consents.termsAndConditionsConsent;
                this.shareEmailConsent = consents.shareEmailConsent;

                await this.$store.dispatch("auth/register", {
                    name: this.name,
                    email: this.email,
                    password: this.password1,
                    remindsMe: this.remindsMe
                });
            } catch (e) {
                this.isPending = false;
                this.error = this.$t("signInUp." + e.message);
            }
        },

        updateRemindsMe(value) {
            this.remindsMe = value;
        },

        validatePwd(value) {
            const numberRegex = /\d/;
            const caseRegex = /(?=.*[a-z])(?=.*[A-Z])/;
            const lengthRegex = /.{8,12}/;
            const noSpaceRegex = /^[^\s]*$/;

            this.passwordValidations.pwdLength = lengthRegex.test(value);
            this.passwordValidations.pwdUpperRequired = caseRegex.test(value);
            this.passwordValidations.pwdNumberRequired = numberRegex.test(value);
            this.passwordValidations.pwdNoSpace = noSpaceRegex.test(value);

            this.passwordInvalid =
                Object.values(this.passwordValidations).find(value => value === false) !==
                undefined;

            this.pwdNoMatch = this.password1 !== this.password2;
        },

        validateEmail(ev: Event) {
            const input = ev.target as HTMLInputElement;
            input.value = input.value.toLowerCase();
        },

        async googleSignIn() {
            try {
                const googleUser = await this.$gAuth.signIn();

                if (!googleUser) {
                    throw new Error("User is null");
                }

                const token = googleUser.getAuthResponse().id_token;
                await this.$store.dispatch("auth/googleSignIn", token);
            } catch (e) {
                console.log("error", e);
                if (e.error === "popup_closed_by_user") {
                    return;
                }

                if (e?.message?.length) {
                    this.error = this.$t("signInUp." + e.message);
                } else {
                    this.error = this.$t("signInUp.unknownError");
                }
            }
        }
    }
});
